import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Icon } from 'ui';
import RenderInBody from '../render_in_body';
import Dismissable from '../dismissable';
import ModalContainer from './modal_container';
import {
  focusLastFocusableNode,
  focusFirstFocusableNode,
} from '../../ui/utilities/focus';

const Modal = props => {
  const expirationDateModalRef = useRef();
  useEffect(() => {
    setTimeout(() => {
      if (expirationDateModalRef.current) {
        focusFirstFocusableNode(expirationDateModalRef.current);
      }
    }, 0);
  }, []);

  const renderConfirmBody = () => {
    const buttonClassName = classNames('NewButton', {
      'NewButton--white': true,
      'NewButton--small': true,
      'NewButton--center': true,
      'Modal-button': true,
    });

    const bodyClassName = classNames('Modal-body', {
      [props.className]: !!props.className,
      'Modal-body--center': props.center,
    });

    const handleConfirm = e => {
      props.onConfirm(e);
      const confirmButtonElement = document.querySelector('#confirmButton');
      confirmButtonElement.setAttribute('disabled', true);
    };

    return (
      <div className={bodyClassName}>
        {props.children}
        <div className="Modal-message">{props.message}</div>
        {props.confirmButtonText && (
          <button
            id="confirmButton"
            type="submit"
            className={buttonClassName}
            onClick={handleConfirm}
          >
            {props.confirmButtonText}
          </button>
        )}
        {props.cancelButtonText && (
          <button
            type="button"
            className={buttonClassName}
            onClick={props.onCancel}
          >
            {props.cancelButtonText}
          </button>
        )}
      </div>
    );
  };

  const containerClassName = classNames(
    'Modal-container',
    props.containerClassName,
    {
      'Modal--wide': props.loading,
    },
  );

  const renderCustomBody = () => {
    const bodyClassName = classNames('Modal-body-0', {
      'p-0': props.noPadding,
    });

    return (
      <div
        ref={expirationDateModalRef}
        className={bodyClassName}
        children={props.children}
      />
    );
  };

  const renderBody = () => {
    const { type } = props;
    if (type === 'confirm' || type === 'confirm:error')
      return renderConfirmBody();
    return renderCustomBody();
  };

  if (!props.show) return false;

  const headerClassName = classNames('Modal-header', props.headerClassName);

  const modalClassName = classNames('Modal', {
    'Modal--danger': props.type === 'confirm:error',
  });
  const handleFocusWrap = first => () => {
    if (expirationDateModalRef.current) {
      if (first) {
        focusLastFocusableNode(expirationDateModalRef.current);
      } else {
        focusFirstFocusableNode(expirationDateModalRef.current);
      }
    }
  };

  return (
    <ModalContainer onEscape={props.dismissCallback}>
      <RenderInBody>
        <div className={modalClassName} key="modal">
          <Dismissable
            styleName={containerClassName}
            dismissCallback={
              props.canClickAway ? props.dismissCallback : () => {}
            }
          >
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            <div
              tabIndex={0}
              aria-hidden="true"
              onFocus={handleFocusWrap(true)}
            />
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
            {props.title && (
              <div className={headerClassName}>
                <div className="Modal-title" children={props.title} />
                {props.dismissCallback && (
                  <>
                    <button
                      type="button"
                      className="Modal-closeIcon u-resetBtn"
                      onClick={props.dismissCallback}
                      aria-label={t('common.close')}
                    >
                      <Icon name="remove" />
                    </button>
                  </>
                )}
              </div>
            )}
            {renderBody()}
            {props.enableFloatingDismiss && (
              <button
                type="button"
                tabIndex={0}
                className="u-resetBtn ModalTakeover-closeButton"
                onClick={props.dismissCallback}
                aria-label={t('common.close')}
              >
                <Icon name="remove" />
              </button>
            )}
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            <div
              tabIndex={0}
              aria-hidden="true"
              onFocus={handleFocusWrap(false)}
            />
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          </Dismissable>
          <div className="Modal-background" />
        </div>
      </RenderInBody>
    </ModalContainer>
  );
};

Modal.defaultProps = {
  dismissCallback() {},
  canClickAway: true,
};

export default Modal;
